/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { setDiff, filter } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal imports
import EditAssessment from 'linced-admin/mixins/edit-assessment';
// External imports
import AssessmentType from '@linc-technologies/linced-common/enums/assessment-type';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class IndexController extends Controller.extend(EditAssessment) {
  AssessmentType = AssessmentType;
  AssessmentDisplay = AssessmentDisplay;

  // Services
  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  @tracked
  editingCategory = null;

  @tracked
  categoryLabel = '';

  @tracked
  categoryDescription = '';

  @action
  setCategoryDescription(value) {
    this.categoryDescription = value;
  }

  @tracked
  parentCategory = null;

  // Deletion attributes
  @tracked
  deleteDialogOpen = false;

  @tracked
  deleteErrorMessage = '';

  @tracked
  deleteInProgress = false;

  @tracked
  deleteLabel = '';

  @tracked
  entityToDelete = null;

  @action
  setCategoryLabel(value) {
    this.categoryLabel = value;
  }

  @tracked
  isDialOpen;

  @action
  setIsDialOpen(value) {
    this.isDialOpen = value;
  }

  @tracked
  selectedTab;

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  // Usage sets
  @tracked
  usageSetDialogOpen = false;

  @tracked
  currentAssessment = null;
  @tracked
  selectedUsageSets = [];
  @tracked
  initialUsageSets = [];
  @tracked
  saveInProgress = false;

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];

  // Computed
  @computed('model.categories.[]', 'selectedUsageSetsFilter.[]')
  get shownCategories() {
    if (!isEmpty(this.selectedUsageSetsFilter)) {
      // Hide all categories when we're filtering by usage set
      // as assessments without the categories will be displayed instead
      return [];
    }
    return this.get('model.categories').filter(function (assessmentCategory) {
      return isEmpty(assessmentCategory.get('parent_id'));
    });
  }

  @computed(
    'model.assessments.@each.{category,usageSetIds}',
    'selectedUsageSetsFilter.[]',
  )
  get filteredAssessments() {
    let selectedUsageSets = this.selectedUsageSetsFilter;
    return this.get('model.assessments').filter(function (assessment) {
      if (!isEmpty(selectedUsageSets)) {
        return selectedUsageSets.some((usageSet) =>
          assessment.get('usageSetsArray').includes(usageSet),
        );
      }
      // Reject duplicates that have categories
      return isEmpty(assessment.get('category.id'));
    });
  }

  // availableSchedules is an array of schedules which are available to the current user.
  @filter('model.gradeschedules', function (schedule) {
    let personId = this.get('session.personId');
    return schedule.get('isShared') || schedule.get('authorId') === personId;
  })
  availableSchedules;

  @tracked
  assessmentDialogOpen;

  @tracked
  categoryDialogOpen;

  @tracked
  categoryErrorMessage;

  @tracked
  categorySaveInProgress;
  @tracked assessmentCategory;

  // recordsReferencing returns the usage sets and page templates that point
  // at an assessment. They get saved after it is deleted so the server drops
  // the stale id; destroyAndSaveRelated explains why they are gathered first.
  async recordsReferencing(assessment) {
    const [usageSets, pageTemplates] = await Promise.all([
      loadRelated(assessment, ['usageSetIds']),
      this.store.query('posts/page', {
        assessmentIds: JSON.stringify([assessment.id]),
      }),
    ]);
    return [...usageSets, ...pageTemplates.slice()];
  }

  // Category management actions.
  @action
  saveCategory() {
    let category = this.editingCategory;
    let store = this.store;
    this.categorySaveInProgress = true;

    // Create a new Category if needed
    if (isEmpty(category)) {
      category = store.createRecord('assessments/category', {
        schoolId: this.get('session.school'),
        parentCategoryId: this.parentCategory,
      });
    }

    // Set properties from form
    category.setProperties({
      label: this.categoryLabel,
      description: this.categoryDescription,
    });

    category
      .save()
      .then(() => {
        this.categoryDialogOpen = false;
      })
      .catch((err) => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(category);
        this.categoryErrorMessage = err;
      })
      .then(() => {
        this.categorySaveInProgress = false;
      });
  }

  @action
  toggleCategoryDialog() {
    this.toggleProperty('categoryDialogOpen');
  }

  @action
  newCategory(parent) {
    this.resetCategoryForm();
    this.parentCategory = parent;
    this.categoryDialogOpen = true;
  }

  @action
  editCategory(category) {
    this.resetCategoryForm();
    this.editingCategory = category;
    this.categoryLabel = category.get('label');
    this.categoryDescription = category.get('description');
    this.categoryDialogOpen = true;
  }

  // Assessment management actions.
  @action
  newAssessment(category) {
    this.resetAssessmentForm();
    this.assessmentCategory = category;
    this.assessmentDialogOpen = true;
  }

  @action
  openAssessment(assessment) {
    this.router.transitionTo('assessments.assessment', assessment.get('id'));
  }

  // Drag-and-drop sorting actions.
  @action
  moveAssessment({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new category - update the relationship.
      item.set('categoryId', targetList.__category__);
    }

    // Update target category's assessment sequences, saving the moved
    // assessment once whether or not its sequence changed.
    targetList.forEach((entity, index) => {
      entity.set('sequence', index);
      if (moved && entity === item) {
        entity.save().catch(() => this.revertMove(entity));
      } else if (entity.get('hasDirtyAttributes')) {
        entity.save();
      }
    });
  }

  @action
  moveCategory({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new parent category - update the relationship.
      item.set('parentCategoryId', targetList.__parent__);
    }

    // Update target category's subcategory sequences, saving the moved
    // category once whether or not its sequence changed.
    targetList.forEach((entity, index) => {
      entity.set('sequence', index);
      if (moved && entity === item) {
        entity.save().catch(() => this.revertMove(entity));
      } else if (entity.get('hasDirtyAttributes')) {
        entity.save();
      }
    });
  }

  // revertMove puts a dragged record back under its old parent when the save
  // fails. The lists are built from the relationships, so it moves back on
  // screen too.
  revertMove(record) {
    rollbackRecord(record);
    this.toaster.error(`Error moving ${record.get('label')}`);
  }

  @action
  closeDeleteDialog() {
    this.deleteDialogOpen = false;
    this.entityToDelete = null;
    this.deleteLabel = '';
    this.deleteErrorMessage = '';
  }

  @action
  deleteCategory(category) {
    this.resetCategoryForm();
    this.deleteDialogOpen = true;
    this.entityToDelete = category;
    this.deleteLabel = 'Assessment Category';
  }

  @action
  deleteAssessment(assessment) {
    this.deleteDialogOpen = true;
    this.entityToDelete = assessment;
    this.deleteLabel = 'Assessment';
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      // Not confirmed the deletion
      this.deleteDialogOpen = false;
      this.entityToDelete = null;
      this.deleteErrorMessage = '';
      this.deleteLabel = '';
      return;
    }

    // Deletion confirmed; actually delete the record.
    this.deleteInProgress = true;
    this.deleteErrorMessage = '';
    let entity = this.entityToDelete;
    try {
      // Only assessments are referenced by usage sets and page templates.
      let related =
        this.deleteLabel === 'Assessment'
          ? await this.recordsReferencing(entity)
          : [];

      // Delete record (locally + pushed to server), then save the records
      // that referenced it.
      let { deleted, error } = await destroyAndSaveRelated(entity, related);
      if (!deleted) {
        this.deleteErrorMessage = deleteErrorMessage(error);
        return;
      }
      if (error) {
        this.toaster.error(
          `${this.deleteLabel} deleted, but removing it from its usage sets and page templates failed.`,
        );
      } else {
        this.toaster.success(`${this.deleteLabel} deleted successfully.`);
      }

      // Tidy up / close dialog.
      this.deleteDialogOpen = false;
      this.entityToDelete = null;
      this.deleteErrorMessage = '';
      this.deleteLabel = '';
    } catch (err) {
      // Finding the referencing records failed; nothing has been deleted.
      this.deleteErrorMessage = deleteErrorMessage(err);
    } finally {
      this.deleteInProgress = false;
    }
  }

  @action
  openUsageSetDialog(assessment) {
    assessment
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.currentAssessment = assessment;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(`Error trying to edit the assessment's usage sets`);
      });
  }

  @action
  toggleUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  saveAssessmentAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );
    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        promises.push(changedUsageSet.save());
      });
    }

    // Save the page template at the end
    this.currentAssessment.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.currentAssessment.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the page and all involved usage sets
        this.toaster.success(
          `Successfully saved the assessment and usage sets`,
        );
        this.currentAssessment.reload();
        this.usageSetDialogOpen = false;
        this.currentAssessment = null;
        this.selectedUsageSets = [];
        this.initialUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the assessment's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.currentAssessment.set('usageSetIds', this.initialUsageSets);
        this.currentAssessment.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  // unpublishAssessment will remove the assessment templates from all usage sets so it becomes unusable
  @action
  unpublishAssessment(assessment) {
    assessment.get('usageSetIds').then((usageSetsArray) => {
      if (!isEmpty(usageSetsArray)) {
        this.selectedUsageSets = [];
        this.initialUsageSets = usageSetsArray.slice();
        this.currentAssessment = assessment;
        this.saveAssessmentAndUsageSet();
      }
    });
  }

  resetCategoryForm() {
    this.categoryErrorMessage = '';
    this.categoryLabel = '';
    this.categoryDescription = '';
    this.parentCategory = null;
    this.editingCategory = null;
  }
}

// deleteErrorMessage picks the message to show for a failed delete.
function deleteErrorMessage(err) {
  if (!isEmpty(err)) {
    if (err.errors && err.errors.length > 0) {
      return err.errors[0].detail;
    }
    if (err.message) {
      return err.message;
    }
  }
  return 'An unknown error occurred. Please try again.';
}
